/**
 * goal.md '## Checks' block (SPEC.md §4, plan-condition syntax §7) — mirrors
 * workspace.parse_goal_checks / plan.evaluate_condition on the server so the
 * editor (views/parts/GoalEditor.tsx) shows exactly what the goal check will
 * evaluate. Condition syntax: doc_exists(<doc_id>), doc_contains(<doc_id>,
 * <regex>), optional leading "not "; "llm:" conditions are plan-mode only
 * and invalid here (agent.py's goal check has no judge and skips them).
 */

const CHECKS_HEADING = "checks";
const NOTE_PREFIX = "Note (";

export type SplitGoal = {
  /** everything before the '## Checks' block — the actual definition of done */
  before: string;
  /** one condition string per '- ...' line inside the block */
  checks: string[];
  /** any '## ...' sections that followed the checks block, kept verbatim */
  after: string;
};

/** Mirrors workspace.parse_goal_checks: stops at the first '## Note (' block so
 * a legacy/forged note-and-heading combination can never smuggle in a check. */
export function splitGoalChecks(text: string): SplitGoal {
  const lines = (text ?? "").split(/\r?\n/);
  const before: string[] = [];
  const checks: string[] = [];
  const after: string[] = [];
  let section: "before" | "checks" | "after" = "before";
  let stopped = false;

  for (const line of lines) {
    if (!stopped && line.startsWith("## ")) {
      const title = line.slice(3).trim();
      if (title.startsWith(NOTE_PREFIX)) {
        stopped = true;
        after.push(line);
        continue;
      }
      section = title.toLowerCase() === CHECKS_HEADING ? "checks" : "after";
      if (section === "after") after.push(line);
      continue;
    }
    if (stopped || section === "after") {
      after.push(line);
      continue;
    }
    if (section === "checks") {
      const trimmed = line.trim();
      if (trimmed.startsWith("- ")) checks.push(trimmed.slice(2).trim());
      continue;
    }
    before.push(line);
  }

  return { before: before.join("\n").trim(), checks, after: after.join("\n").trim() };
}

export function joinGoalChecks(split: SplitGoal): string {
  let out = split.before.trimEnd();
  if (split.checks.length > 0) {
    out += "\n\n## Checks\n" + split.checks.map((c) => `- ${c}`).join("\n");
  }
  out += "\n";
  if (split.after.trim()) {
    out += "\n" + split.after.trim() + "\n";
  }
  return out;
}

export type CheckKind = "doc_exists" | "doc_contains";

/** Mirrors plan._COND_RE — used to flag conditions the server would skip. */
const COND_RE = /^(not\s+)?(doc_exists|doc_contains)\((.*)\)$/;

export function isValidCondition(condition: string): boolean {
  const trimmed = condition.trim();
  if (!trimmed || trimmed.startsWith("llm:")) return false;
  const match = COND_RE.exec(trimmed);
  if (!match) return false;
  const [, , predicate, rawArgs] = match;
  const args = rawArgs.split(",");
  if (predicate === "doc_exists") return args.length === 1 && args[0].trim() !== "";
  return args.length >= 2 && args[0].trim() !== "" && rawArgs.split(/,(.*)/s)[1]?.trim() !== "";
}

export function formatCondition(kind: CheckKind, docId: string, pattern: string, negate: boolean): string {
  const id = docId.trim();
  const inner = kind === "doc_exists" ? `doc_exists(${id})` : `doc_contains(${id}, ${pattern.trim()})`;
  return negate ? `not ${inner}` : inner;
}
